import { emptyPaymentFields, hasPaymentMethod, resolveFormSelfPayment } from "./payment";
import { toDatetimeLocalValue } from "./eventStatus";
import { instagramHandleForInput } from "./instagram";
import { validateOptionalContactFields } from "./contactValidation";
import { normalizeExternalSchoolUrl } from "./hostSchool";

export const CREATE_EVENT_FORM_ID = "create-event-form";

export const CREATE_EVENT_FIELD_ORDER = [
  "name",
  "organizer_name",
  "host_school",
  "city",
  "location",
  "starts_at",
  "pricing_amount",
  "early_bird_amount",
  "early_bird_cutoff",
  "early_bird_rules",
  "early_bird_max_rsvps",
  "self_payment",
  "self_payment_phone",
  "self_payment_email",
];

export const CREATE_EVENT_FIELD_IDS = {
  name: "create-event-name",
  organizer_name: "create-event-organizer",
  host_school: "host-school-promotion-field",
  city: "create-event-city",
  location: "create-event-location",
  starts_at: "create-event-starts-at",
  pricing_amount: "create-event-pricing-amount",
  pricing_currency: "create-event-pricing-currency",
  early_bird_amount: "create-event-early-bird-amount",
  early_bird_cutoff: "create-event-early-bird-cutoff",
  early_bird_rules: "create-event-early-bird-rules",
  early_bird_max_rsvps: "create-event-early-bird-max-rsvps",
  self_payment: "event-payment-form",
  map_link: "create-event-map-link",
  capacity: "create-event-capacity",
  leader_cap: "create-event-leader-cap",
  follower_cap: "create-event-follower-cap",
  instagram_url: "create-event-instagram-url",
  recurring: "create-event-recurring",
  access_paid_note: "create-event-access-paid-note",
};

export const EMPTY_FORM = {
  name: "",
  description: "",
  city: "",
  city_location: null,
  location: "",
  location_map_link: "",
  latitude: null,
  longitude: null,
  starts_at: "",
  door_time: "",
  recurring: "none",
  recurring_end: "",
  pricing_type: "free",
  pricing_amount: "",
  pricing_currency: "EUR",
  early_bird_enabled: false,
  early_bird_amount: "",
  early_bird_cutoff: "",
  early_bird_max_rsvps: "",
  instagram_url: "",
  access: "open",
  capacity: "",
  leader_cap: "",
  follower_cap: "",
  organizer_name: "",
  /** 0–1 guest co-host: `{ dancer_id, name }` selected from RSVPs. */
  co_hosts: [],
  cover_color: "pink",
  school_id: null,
  school_external_name: "",
  school_external_url: "",
  host_school_opt_out: false,
  self_payment: { use_profile_defaults: true, ...emptyPaymentFields(null) },
};

export const CREATE_EVENT_CO_HOST_FIELD_ID = "create-event-co-host";

/** Guests eligible to be co-host: going or maybe, not the primary host. */
export function coHostCandidateGuests(rsvps = [], { hostNames = [] } = {}) {
  const excluded = new Set(
    hostNames
      .map((n) => String(n || "").trim().toLowerCase())
      .filter(Boolean),
  );
  return (rsvps || []).filter((r) => {
    const status = r?.attendance_status || "likely";
    if (status !== "likely" && status !== "maybe") return false;
    const name = String(r?.name || "").trim().toLowerCase();
    if (name && excluded.has(name)) return false;
    return Boolean(r?.dancer_id);
  });
}

export function resolveCoHostFromGuests(coHosts, guests = []) {
  const saved = Array.isArray(coHosts) && coHosts[0] ? coHosts[0] : null;
  if (!saved) return [];
  if (saved.dancer_id) {
    const byId = guests.find((g) => g.dancer_id === saved.dancer_id);
    if (byId) return [{ dancer_id: byId.dancer_id, name: byId.name || saved.name || "" }];
  }
  const savedName = String(saved.name || "").trim().toLowerCase();
  if (savedName) {
    const byName = guests.find((g) => String(g.name || "").trim().toLowerCase() === savedName);
    if (byName) return [{ dancer_id: byName.dancer_id, name: byName.name || saved.name || "" }];
  }
  // Keep name-only until guests load / match (legacy free-text co-hosts).
  if (saved.name?.trim()) return [{ dancer_id: saved.dancer_id || "", name: saved.name.trim() }];
  return [];
}

export function coHostsPayloadFromForm(form, guests = []) {
  const selected = Array.isArray(form?.co_hosts) ? form.co_hosts[0] : null;
  if (!selected) return [];
  const guest = selected.dancer_id
    ? guests.find((g) => g.dancer_id === selected.dancer_id)
    : null;
  const name = String(guest?.name || selected.name || "").trim();
  if (!name) return [];
  return [{ name }];
}

/** Option label for co-host select — truncates long names, marks tentative RSVPs. */
export function coHostOptionLabel(guest, { maxNameLen = 48 } = {}) {
  const raw = String(guest?.name || "Guest").trim() || "Guest";
  const limit = Math.max(8, maxNameLen);
  const name = raw.length > limit ? `${raw.slice(0, limit - 1)}…` : raw;
  const maybe = (guest?.attendance_status || "likely") === "maybe";
  return maybe ? `${name} · Maybe coming` : name;
}

export function isMaybeCoHostGuest(guest) {
  return (guest?.attendance_status || "likely") === "maybe";
}

export function pricingForcesInviteOnly(pricingType) {
  return pricingType === "paid-self-managed";
}

/**
 * True when create/edit form has content worth protecting before leaving
 * (user-entered fields, existing draft/edit). Profile-only prefills do not count.
 */
export function createEventFormNeedsLeaveConfirm(form, { isEdit = false, isDraft = false } = {}) {
  if (isEdit || isDraft) return true;
  if (!form) return false;

  const hasText = (value) => Boolean(String(value || "").trim());
  if (hasText(form.name)) return true;
  if (hasText(form.description)) return true;
  if (hasText(form.location)) return true;
  if (hasText(form.location_map_link)) return true;
  if (hasText(form.starts_at)) return true;
  if (hasText(form.door_time)) return true;
  if (hasText(form.recurring_end)) return true;
  if (form.recurring && form.recurring !== "none") return true;
  if (form.pricing_type && form.pricing_type !== "free") return true;
  if (hasText(form.pricing_amount)) return true;
  if (form.early_bird_enabled) return true;
  if (hasText(form.early_bird_amount) || hasText(form.early_bird_cutoff) || hasText(form.early_bird_max_rsvps)) {
    return true;
  }
  if (hasText(form.instagram_url)) return true;
  if (form.access && form.access !== "open") return true;
  if (hasText(form.capacity) || hasText(form.leader_cap) || hasText(form.follower_cap)) return true;
  if (Array.isArray(form.co_hosts) && form.co_hosts.some((c) => c?.dancer_id || String(c?.name || "").trim())) {
    return true;
  }
  // school_id alone does not count — a single managed school is auto-linked like a profile prefill.
  if (form.host_school_opt_out) return true;
  if (String(form.school_external_name || "").trim()) return true;
  if (String(form.school_external_url || "").trim()) return true;
  if (form.cover_color && form.cover_color !== EMPTY_FORM.cover_color) return true;

  const sp = form.self_payment || {};
  if (sp.use_profile_defaults === false) {
    if (
      hasText(sp.payment_link)
      || hasText(sp.payment_upi)
      || hasText(sp.contact_phone)
      || hasText(sp.contact_email)
      || hasText(sp.contact_telegram)
      || hasText(sp.instructions)
    ) {
      return true;
    }
  }

  return false;
}

function normalizeDoorTime(value) {
  if (!value) return "";
  if (String(value).includes("T")) {
    try {
      return new Date(value).toTimeString().slice(0, 5);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function eventToForm(ev) {
  const p = ev.pricing || { type: "free" };
  const sp = ev.self_payment || {};
  return {
    name: ev.name || "",
    description: ev.description || "",
    city: ev.city || "",
    city_location: ev.city_location || null,
    location: ev.location || "",
    location_map_link: ev.location_map_link || "",
    starts_at: toDatetimeLocalValue(ev.starts_at),
    door_time: normalizeDoorTime(ev.door_time),
    recurring: ev.recurring || "none",
    recurring_end: ev.recurring_end ? toDatetimeLocalValue(ev.recurring_end) : "",
    pricing_type: p.type === "early-bird" ? "paid-at-venue" : (p.type || "free"),
    pricing_amount: p.amount != null ? String(p.amount) : "",
    pricing_currency: p.currency || "EUR",
    early_bird_enabled: Boolean(p.early_bird_amount),
    early_bird_amount: p.early_bird_amount != null ? String(p.early_bird_amount) : "",
    early_bird_cutoff: p.early_bird_cutoff ? toDatetimeLocalValue(p.early_bird_cutoff) : "",
    early_bird_max_rsvps:
      p.early_bird_max_rsvps != null
        ? String(p.early_bird_max_rsvps)
        : p.early_bird_rsvp_cap != null
          ? String(p.early_bird_rsvp_cap)
          : "",
    instagram_url: instagramHandleForInput(ev.instagram_url),
    access: ev.access || "open",
    capacity: ev.capacity != null ? String(ev.capacity) : "",
    leader_cap: ev.leader_cap != null ? String(ev.leader_cap) : "",
    follower_cap: ev.follower_cap != null ? String(ev.follower_cap) : "",
    organizer_name: ev.organizer_name || "",
    co_hosts: (ev.co_hosts || [])
      .slice(0, 1)
      .map((c) => ({
        dancer_id: "",
        name: c.name || "",
      }))
      .filter((c) => c.name),
    cover_color: ev.cover_color || "pink",
    school_id: ev.school_id || null,
    school_external_name: ev.school_external_name || "",
    school_external_url: ev.school_external_url || "",
    host_school_opt_out: Boolean(ev.host_school_opt_out),
    self_payment: {
      use_profile_defaults: sp.use_profile_defaults !== false,
      payment_link: sp.payment_link || "",
      payment_upi: sp.payment_upi || "",
      instructions: sp.instructions || "",
      contact_phone: sp.contact_phone || "",
      contact_email: sp.contact_email || "",
      contact_telegram: sp.contact_telegram || "",
    },
  };
}

export function scrollToFirstFieldError(errors) {
  const reduceMotion =
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  for (const key of CREATE_EVENT_FIELD_ORDER) {
    if (!errors[key]) continue;
    const el = document.getElementById(CREATE_EVENT_FIELD_IDS[key]);
    if (!el) continue;
    el.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth", block: "center" });
    if (typeof el.focus === "function") {
      try {
        el.focus({ preventScroll: true });
      } catch {
        el.focus();
      }
    }
    break;
  }
}

export function validateCreateEventForm(
  form,
  { account, organizer, isDraft }
) {
  organizer = account ?? organizer;
  const errors = {};
  const paid =
    form.pricing_type === "paid-at-venue" || form.pricing_type === "paid-self-managed";

  if (!form.name?.trim()) errors.name = "Enter an event name.";
  if (!form.organizer_name?.trim()) errors.organizer_name = "Enter the host name.";
  if (!form.city?.trim()) errors.city = "Enter a city.";
  else if (!form.city_location) errors.city = "Select your city from the suggestions.";
  if (!form.location?.trim()) errors.location = "Enter the venue name.";
  if (!form.starts_at) errors.starts_at = "Choose a date and start time.";

  if (paid && (!form.pricing_amount || parseFloat(form.pricing_amount) <= 0)) {
    errors.pricing_amount = "Enter the ticket price.";
  }

  if (paid && form.early_bird_enabled) {
    const eb = parseFloat(form.early_bird_amount);
    const regular = parseFloat(form.pricing_amount);
    if (!Number.isFinite(eb) || eb <= 0) {
      errors.early_bird_amount = "Enter the early bird price.";
    } else if (!Number.isFinite(regular) || eb >= regular) {
      errors.early_bird_amount = "Early bird price must be lower than the regular price.";
    }
    if (!form.early_bird_cutoff && !form.early_bird_max_rsvps) {
      errors.early_bird_rules = "Set a deadline, a spot limit, or both.";
    }
    if (form.early_bird_max_rsvps) {
      const cap = parseInt(form.early_bird_max_rsvps, 10);
      if (!Number.isFinite(cap) || cap <= 0) {
        errors.early_bird_max_rsvps = "Enter a number greater than zero.";
      }
    }
  }

  if (form.pricing_type === "paid-self-managed") {
    const sp = form.self_payment || {};
    const effective = resolveFormSelfPayment(sp, organizer);
    if (!hasPaymentMethod(effective)) {
      errors.self_payment = "Add a payment link or UPI ID in your profile or below.";
    }
    if (effective.use_profile_defaults === false) {
      Object.assign(
        errors,
        validateOptionalContactFields(
          { phone: sp.contact_phone, email: sp.contact_email },
          { phoneKey: "self_payment_phone", emailKey: "self_payment_email" },
        ),
      );
    }
  }

  if (!isDraft && !form.host_school_opt_out) {
    const externalName = String(form.school_external_name || "").trim();
    const externalUrlRaw = String(form.school_external_url || "").trim();
    const externalUrl = normalizeExternalSchoolUrl(externalUrlRaw);
    if (form.school_id) {
      if (externalName || externalUrlRaw) {
        errors.host_school = "Choose either your rsvp.dance school or an external link, not both.";
      }
    } else if (externalName || externalUrlRaw) {
      if (!externalName) errors.host_school = "Enter the school name.";
      else if (!externalUrl) errors.host_school = "Enter a valid school website URL.";
    } else {
      errors.host_school = "Link your school, add an external link, or opt out of school promotion.";
    }
  }

  return errors;
}
